// SSRF false positive: fetch(ctx.url) host is the build-time VITE_API_URL /
// fixed '/api/' base; path interpolation cannot change the origin.
// fallow-ignore-file security-sink
import type {
  HttpError,
  HttpResponse,
  RequestConfig
} from '../http-client-types'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

export interface RequestContext {
  url: string
  method: HttpMethod
  headers: Record<string, string>
}

export type RequestMiddleware = (
  context: RequestContext
) => Promise<RequestContext> | RequestContext

export interface CreateHttpClientOptions {
  baseURL: string
  defaultHeaders?: Record<string, string>
}

function joinUrl(baseURL: string, path: string): string {
  if (!baseURL) return path
  if (!path) return baseURL
  const baseEndsWithSlash = baseURL.endsWith('/')
  const pathStartsWithSlash = path.startsWith('/')
  if (baseEndsWithSlash && pathStartsWithSlash) return baseURL + path.slice(1)
  if (!baseEndsWithSlash && !pathStartsWithSlash) return `${baseURL}/${path}`
  return baseURL + path
}

function buildUrl(
  baseURL: string,
  path: string,
  params: RequestConfig['params']
): string {
  const url = joinUrl(baseURL, path)
  if (!params) return url
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  if (!qs) return url
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`
}

async function parseSuccessBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T
  const contentLength = response.headers.get('Content-Length')
  if (contentLength === '0') return undefined as T
  const text = await response.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    return text as unknown as T
  }
}

/**
 * Read a non-ok `Response` body into a shape suitable for the `data` field
 * of {@link HttpError}. Returns `null` for empty bodies, a parsed object for
 * valid JSON (including `ProblemDetails`), and the raw text for non-JSON
 * bodies (e.g. an HTML error page from a reverse proxy). Safe to call once
 * per response — does not rewind the body stream.
 *
 * Exported so code paths that can't go through `createHttpClient` (e.g. the
 * support-session token endpoint, which is a bootstrap call that would loop
 * back through the auth middleware) can parse errors with identical
 * semantics instead of hand-rolling a subtly different version.
 */
export async function parseErrorBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '')
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * Framework-agnostic HTTP client factory backed by native `fetch`.
 *
 * Middleware registered via `use()` runs in order before each request and can
 * mutate the URL, method, or headers. Non-2xx responses throw a plain
 * {@link HttpError} — consumers are expected to map these to `AppError` via
 * `mapToAppError` at the boundary where the caller lives.
 */
export function createHttpClient(options: CreateHttpClientOptions) {
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.defaultHeaders
  }
  const middlewares: RequestMiddleware[] = []

  function setHeader(key: string, value: string) {
    defaultHeaders[key] = value
  }

  function setHeaders(headers: Record<string, string>) {
    for (const [key, value] of Object.entries(headers)) setHeader(key, value)
  }

  function removeHeader(key: string) {
    delete defaultHeaders[key]
  }

  function getHeaders(): Readonly<Record<string, string>> {
    return { ...defaultHeaders }
  }

  function use(fn: RequestMiddleware) {
    middlewares.push(fn)
  }

  async function request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<HttpResponse<T>> {
    let ctx: RequestContext = {
      url: buildUrl(options.baseURL, path, config?.params),
      method,
      headers: { ...defaultHeaders, ...config?.headers }
    }

    for (const mw of middlewares) {
      ctx = await mw(ctx)
    }

    let response: Response
    try {
      response = await fetch(ctx.url, {
        method: ctx.method,
        headers: ctx.headers,
        body:
          body !== undefined && body !== null
            ? JSON.stringify(body)
            : undefined,
        signal: config?.signal
      })
    } catch (err) {
      const networkError: HttpError = {
        status: 0,
        data: null,
        message: err instanceof Error ? err.message : 'Network request failed'
      }
      throw networkError
    }

    if (!response.ok) {
      const data = await parseErrorBody(response)
      const httpError: HttpError = {
        status: response.status,
        data,
        message: response.statusText || `HTTP ${response.status}`
      }
      throw httpError
    }

    const data = await parseSuccessBody<T>(response)
    return { data, status: response.status, headers: response.headers }
  }

  return {
    setHeader,
    setHeaders,
    removeHeader,
    getHeaders,
    use,
    request,
    get: <T>(path: string, config?: RequestConfig) =>
      request<T>('GET', path, undefined, config),
    post: <T>(path: string, body?: unknown, config?: RequestConfig) =>
      request<T>('POST', path, body, config),
    put: <T>(path: string, body?: unknown, config?: RequestConfig) =>
      request<T>('PUT', path, body, config),
    delete: <T>(path: string, body?: unknown, config?: RequestConfig) =>
      request<T>('DELETE', path, body, config),
    patch: <T>(path: string, body?: unknown, config?: RequestConfig) =>
      request<T>('PATCH', path, body, config)
  }
}

export type HttpClient = ReturnType<typeof createHttpClient>
